import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { motion, useScroll, useTransform } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const HeroSection: React.FC = () => {
  const theme = useTheme();
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 500], [0, 100]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.5]);

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: '90vh', md: '85vh' },
        minHeight: { xs: 500, md: 650 },
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Background image with parallax effect */}
      <motion.div
        style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
          y: backgroundY,
          opacity
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/images/hero/hero-background.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              background: 'linear-gradient(to right, rgba(26, 43, 60, 0.85) 0%, rgba(26, 43, 60, 0.7) 50%, rgba(26, 43, 60, 0.6) 100%)',
            },
          }}
        />
      </motion.div>

      <Container 
        maxWidth="lg"
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '100%', md: '55%' },
            pr: { md: 5 },
            position: 'relative',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 600,
                mb: 1,
              }}
            >
              PROFESSIONAL TRANSPORTATION
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          >
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                lineHeight: { xs: 1.2, md: 1.1 },
                mb: 2,
              }}
            >
              Standard to Luxury Transportation Services
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={{
                fontWeight: 400,
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: 600,
              }}
            >
              Enhance your travel experience with our diverse fleet and professional drivers. 
              From affordable standard options to premium luxury vehicles - reliable, secure, 
              and comfortable transportation for all clients.
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: "easeOut" }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 3 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  component={RouterLink}
                  to="/services"
                  variant="outlined"
                  color="inherit"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderWidth: 2,
                    fontWeight: 600,
                    '&:hover': {
                      borderWidth: 2,
                    },
                  }}
                >
                  Our Services
                </Button>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ display: 'none' }}
              >
                <Button
                  component={RouterLink}
                  to="/booking"
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Book Now
                </Button>
              </motion.div>
            </Stack>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: "easeOut" }}
            whileHover={{ scale: 1.02 }}
            style={{ display: 'none' }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 5,
              }}
            >
              <Button
                variant="text"
                color="inherit"
                startIcon={
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                    }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 2,
                      ease: "easeInOut" 
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'secondary.main',
                        color: 'primary.main',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        mr: 1,
                      }}
                    >
                      <PlayArrowIcon />
                    </Box>
                  </motion.div>
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}
              >
                Watch Our Corporate Fleet Tour
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Container>

      {/* Decorative element */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.2, scale: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: { xs: '150px', md: '300px' },
            height: { xs: '150px', md: '300px' },
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
            borderTopLeftRadius: '100%',
          }}
        />
      </motion.div>
    </Box>
  );
};

export default HeroSection; 